import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import { SxProps, Theme } from '@mui/material'
import CloseIcon from '../../../icons/closeIcon'
import ConfirmIcon from '../../../icons/confirmIcon'
import WarningIcon from '../../../icons/warningIcon'

interface Props {
  open: boolean
  handleClose: () => void
  children: React.ReactNode
  sx?: SxProps<Theme>
  backdropClick?: boolean
  variant?: 'default' | 'wide' | 'narrow'
  title?: string
  titleType?: 'warning' | 'confirm'
}

const icons = {
  confirm: <ConfirmIcon />,
  warning: <WarningIcon />,
}

export default function CustomModal({ 
  open, 
  handleClose, 
  children, 
  sx, 
  backdropClick = false,
  variant = 'default',
  title,
  titleType
}: Props) {
  const closeHandling = (event: React.SyntheticEvent, reason: string) => {
    if (backdropClick && reason && reason == 'backdropClick') 
      return;
    handleClose()
  }

  // Helper function to format title for mobile line breaks
  const formatTitleForMobile = (title: string) => {
    if (title === 'Modifier les informations d\'un utilisateur') {
      return (
        <>
          <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
            Modifier les informations
          </Box>
          <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
            {' '}d'un utilisateur
          </Box>
        </>
      )
    }
    return title
  }

  const getVariantStyles = () => {
    // Base styles for form modals (narrow variant)
    const formModalStyles = {
      position: 'absolute' as const,
      top: {
        xs: 'max(env(safe-area-inset-top), 8px)', // Respect device safe areas
        md: '50%',
      },
      left: '50%',
      transform: {
        xs: 'translate(-50%, 0)', // Don't center vertically on mobile
        md: 'translate(-50%, -50%)',
      },
      height: {
        xs: 'calc(100dvh - max(env(safe-area-inset-top), 8px) - max(env(safe-area-inset-bottom), 16px))', // Use dynamic viewport height
        md: 'auto',
      },
      maxHeight: {
        xs: 'calc(100dvh - max(env(safe-area-inset-top), 8px) - max(env(safe-area-inset-bottom), 16px))', // Account for safe areas
        md: 'calc(100vh - 80px)',
      },
      minHeight: '150px',
      bgcolor: 'white',
      boxShadow: 24,
      outline: 'none',
      padding: 0, 
      borderRadius: {
        xs: '12px',
        md: '8px',
      },
      overflow: 'hidden', 
      display: 'flex',
      flexDirection: 'column' as const,
    }

    const confirmationModalStyles = {
      position: 'absolute' as const,
      top: '50%', 
      left: '50%',
      transform: 'translate(-50%, -50%)', // Center both horizontally and vertically
      height: 'auto', // Auto height for all screen sizes
      maxHeight: {
        xs: '80vh', // Limit to 80% of viewport height on mobile
        md: 'calc(100vh - 80px)',
      },
      minHeight: '150px',
      bgcolor: 'white',
      boxShadow: 24,
      outline: 'none',
      padding: 0, 
      borderRadius: {
        xs: '12px',
        md: '8px',
      },
      overflow: 'hidden', 
      display: 'flex',
      flexDirection: 'column' as const,
    }

    switch (variant) {
      case 'wide':
        return {
          ...confirmationModalStyles,
          width: {
            xs: 'calc(100vw - 24px)',
            sm: '90vw',
            md: '800px',
          },
          maxWidth: {
            xs: '500px',
            md: '800px'
          }
        }
      case 'narrow':
        return {
          ...formModalStyles,
          width: {
            xs: 'calc(100vw - 32px)',
            sm: '70vw',
            md: '400px',
          },
          maxWidth: {
            xs: '350px',
            md: '400px'
          }
        }
      default:
        return {
          ...confirmationModalStyles,
          width: {
            xs: 'calc(100vw - 32px)',
            sm: '80vw',
            md: '600px',
          },
          maxWidth: {
            xs: '450px',
            md: '600px'
          }
        }
    }
  }

  return (
    <Modal
      open={open}
      onClose={closeHandling}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description' 
    >
      <Box
        sx={{
          ...getVariantStyles(),
          ...sx,
        }}
      >
        {title ? (
          <Box
            sx={{
              position: 'relative',
              top: 0,
              zIndex: 10,
              bgcolor: 'white',
              padding: {
                xs: '16px',
                sm: '20px',
                md: '24px',
              },
              paddingBottom: {
                xs: '8px',
                sm: '12px',
                md: '16px',
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderTopLeftRadius: {
                xs: '12px',
                md: '8px',
              },
              borderTopRightRadius: {
                xs: '12px',
                md: '8px',
              },
              borderBottom: '0.5px solid #E0E0E0',
              boxSizing: 'border-box',
              gap: '8px',
            }}
          >
            <Box
              component='button'
              sx={{
                position: 'absolute',
                top: {
                  xs: '16px',
                  sm: '20px', 
                  md: '24px',
                },
                right: {
                  xs: '16px',
                  sm: '20px',
                  md: '24px',
                },
                border: 'none',
                outline: 'none',
                background: 'none',
                cursor: 'pointer',
                p: '4px',
                width: '24px',
                height: '24px',
                boxShadow: 'none',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
              onClick={handleClose}
            >
              <CloseIcon />
            </Box>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '8px',
              width: '100%',
              paddingRight:'32px',
            }}>
              {titleType && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {icons[titleType]}
                </Box>
              )}
              <Typography 
                variant='h4' 
                sx={{ 
                  fontSize: {
                    xs: '8px',
                    sm: '16px',
                    md: '24px'
                  },
                  fontWeight: 'bold',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {formatTitleForMobile(title)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              padding: {
                xs: '8px 16px',
                sm: '8px 20px',
                md: '8px 24px',
              },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              borderTopLeftRadius: {
                xs: '12px',
                md: '8px',
              },
              borderTopRightRadius: {
                xs: '12px',
                md: '8px',
              },
            }}
          >
            <Box
              component='button'
              sx={{
                border: 'none',
                outline: 'none',
                background: 'none',
                cursor: 'pointer',
                p: '4px',
                width: '24px',
                height: '24px',
                boxShadow: 'none',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
              onClick={handleClose}
            >
              <CloseIcon />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: {
              xs: '0 16px',
              sm: '0 20px',
              md: '0 24px',
            },
            paddingBottom: '16px',
            '&::-webkit-scrollbar': {
              display: 'none',
              width: '0px',
              background: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              display: 'none',
            },
            '&::-webkit-scrollbar-thumb': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            scrollbarGutter: 'stable',
           
            '& .MuiButton-root': {
              borderRadius: '0px',
              minHeight: {
                xs: '40px',
                sm: '44px',
                md: '48px',
              },
              fontSize: {
                xs: '14px',
                sm: '15px',
                md: '16px',
              },
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              '&:hover': {
                boxShadow: 'none',
              },
            },
          }}
        >
          {children}
        </Box>
      </Box>
    </Modal>
  )
}
